import { useEffect, useMemo, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { directoryApi, STAFF_ROLES, type StaffDirectoryEntry, type StaffRole } from "@/lib/api";
import { staffLevelLabel } from "@/lib/users-data";
import { useAuth } from "@/stores/auth";
import { Plus, Search, Pencil, Trash2, ShieldCheck, ShieldAlert, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";

type StaffStatus = StaffDirectoryEntry["status"];

const levelTone: Record<number, string> = {
  1: "bg-muted text-foreground border-border",
  2: "bg-info/10 text-info border-info/20",
  3: "bg-primary/10 text-primary border-primary/20",
};
const levelIcon: Record<number, typeof Shield> = { 1: Shield, 2: ShieldAlert, 3: ShieldCheck };

const roleLabel: Record<StaffRole, string> = {
  superadmin: "Super admin",
  admin: "Admin",
  dispatch_officer: "Dispatch officer",
  driver: "Driver",
  support: "Support",
};
const roleTone: Record<StaffRole, string> = {
  superadmin: "bg-primary/10 text-primary border-primary/20",
  admin: "bg-info/10 text-info border-info/20",
  dispatch_officer: "bg-muted text-foreground border-border",
  driver: "bg-muted text-foreground border-border",
  support: "bg-warning/10 text-warning-foreground border-warning/30",
};

type Form = { name: string; email: string; status: StaffStatus; role: StaffRole; password: string };
const empty: Form = { name: "", email: "", status: "active", role: "dispatch_officer", password: "" };

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*";
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

export default function StaffPage() {
  const myRoles = useAuth((s) => s.session?.roles) ?? [];
  const token = useAuth((s) => s.session?.accessToken);
  const canManageStaff = myRoles.includes("superadmin") || myRoles.includes("admin");
  const [items, setItems] = useState<StaffDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState<"all" | "1" | "2" | "3">("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StaffDirectoryEntry | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [del, setDel] = useState<StaffDirectoryEntry | null>(null);

  useEffect(() => {
    let cancelled = false;
    directoryApi
      .staff(token)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => toast.error("Failed to load staff"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(() => items.filter((u) => {
    if (level !== "all" && String(u.level) !== level) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      u.name.toLowerCase().includes(s) ||
      (u.email?.toLowerCase().includes(s) ?? false) ||
      (u.job_title?.toLowerCase().includes(s) ?? false)
    );
  }), [items, q, level]);

  const openCreate = () => {
    if (!canManageStaff) return;
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (u: StaffDirectoryEntry) => {
    if (!canManageStaff) return;
    setEditing(u);
    setForm({ name: u.name, email: u.email ?? "", status: u.status, role: u.roles[0] ?? "dispatch_officer", password: "" });
    setOpen(true);
  };
  const save = async () => {
    if (!canManageStaff) return;
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email required");
    if (!editing && !form.password.trim()) return toast.error("Generate a password first");
    try {
      if (editing) {
        const updated = await directoryApi.updateStaff(editing.id, {
          full_name: form.name,
          email: form.email,
          status: form.status,
          role: form.role,
          password: form.password || undefined,
        }, token);
        setItems((cur) => cur.map((u) => (u.id === editing.id ? updated : u)));
        toast.success("Staff updated");
      } else {
        const created = await directoryApi.createStaff({
          full_name: form.name,
          email: form.email,
          role: form.role,
          password: form.password,
        }, token);
        setItems((cur) => [created, ...cur]);
        toast.success("Staff added");
      }
      setOpen(false);
    } catch {
      toast.error(editing ? "Could not update staff" : "Could not add staff");
    }
  };
  const remove = async () => {
    if (!del || !canManageStaff) return;
    try {
      await directoryApi.deleteStaff(del.id, token);
      setItems((cur) => cur.filter((u) => u.id !== del.id));
      toast.success(`Removed ${del.name}`);
    } catch {
      toast.error("Could not remove staff");
    } finally {
      setDel(null);
    }
  };

  const counts = { l1: items.filter((u) => u.level === 1).length, l2: items.filter((u) => u.level === 2).length, l3: items.filter((u) => u.level === 3).length };

  return (
    <>
      <Topbar
        title="Staff"
        subtitle={`${items.length} team members · L1 ${counts.l1} · L2 ${counts.l2} · L3 ${counts.l3}`}
        actions={canManageStaff ? <Button size="sm" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Add staff</Button> : undefined}
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          {([1, 2, 3] as const).map((L) => {
            const Icon = levelIcon[L];
            const c = items.filter((u) => u.level === L).length;
            return (
              <div key={L} className="surface-card p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{staffLevelLabel[L as 1 | 2 | 3]}</span>
                  <Icon className={cn("h-4 w-4", L === 3 && "text-primary")} />
                </div>
                <div className="mt-1 text-2xl font-bold">{c}</div>
                <div className="text-[11px] text-muted-foreground">
                  {L === 1 && "Frontline operators"}
                  {L === 2 && "Team leads · reviewers"}
                  {L === 3 && "Executives · highest access"}
                </div>
              </div>
            );
          })}
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, title…" className="pl-9" />
            </div>
            <div className="flex gap-2">
              <Select value={level} onValueChange={(v) => setLevel(v as typeof level)}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="1">L1 · Associate</SelectItem>
                  <SelectItem value="2">L2 · Lead</SelectItem>
                  <SelectItem value="3">L3 · Executive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7}><LoadingBlock label="Loading staff…" /></TableCell></TableRow>
              ) : (
                <>
                  {filtered.map((u) => {
                    const Icon = levelIcon[u.level] ?? Shield;
                    return (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-[11px] text-muted-foreground">{u.email ?? "—"}</div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{u.job_title ?? "—"}</TableCell>
                        <TableCell>
                          <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium", levelTone[u.level] ?? levelTone[1])}>
                            <Icon className="h-3 w-3" /> {staffLevelLabel[u.level as 1 | 2 | 3] ?? `L${u.level}`}
                          </span>
                        </TableCell>
                        <TableCell>
                          {u.roles.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {u.roles.map((r) => (
                                <span key={r} className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium", roleTone[r])}>
                                  {roleLabel[r]}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{u.joined_at ? new Date(u.joined_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>
                          <span className={cn(
                            "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
                            u.status === "active" ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground border-border",
                          )}>{u.status}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {canManageStaff ? (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => setDel(u)} className="text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No staff match.</TableCell></TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "Add staff"}</DialogTitle>
            <DialogDescription>Internal team member with role-based access.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div>
              <Label>Password</Label>
              <div className="flex gap-2">
                <Input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? "Leave blank to keep current password" : "Generate a password"}
                />
                <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: generatePassword() })}>
                  Generate
                </Button>
              </div>
              {form.password ? (
                <p className="mt-1 text-[11px] text-muted-foreground">Share this with the new hire — it won't be shown again.</p>
              ) : null}
            </div>
            <div>
              <Label>Role (access control)</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as StaffRole })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAFF_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as StaffStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This revokes access immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
